import React, { useState, useEffect, useMemo } from "react";
import RichTextViewer from "../../components/common/RichTextViewer";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Clock,
  Users,
  Trophy,
} from "lucide-react";
import { useGlobalState } from "../../GlobalStates";
import { getCourse, listEnrollments, getEnrollmentAttempts } from "../../services/courseService";
import { getDisplayName, getFullName } from "../../utils/userDisplayName";

const StatusBadge = ({ status }) => {
  if (status === "completed") {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-green-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-700">
        <Check className="tw-h-3 tw-w-3" /> Completed
      </span>
    );
  }
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-yellow-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-yellow-700">
      <Clock className="tw-h-3 tw-w-3" /> In Progress
    </span>
  );
};

const GRADED_TYPES = ["single_select", "multi_select", "true_false"];

const formatAnswer = (answer, question) => {
  if (answer == null || answer === "") return "—";
  if (Array.isArray(answer)) {
    return answer
      .map((a) => {
        const opt = (question.options || []).find((o) => o.id === a);
        return opt ? opt.text : a;
      })
      .join(", ");
  }
  if (typeof answer === "string" && question.options) {
    const opt = (question.options || []).find((o) => o.id === answer);
    return opt ? opt.text : answer;
  }
  return typeof answer === "string" ? answer : JSON.stringify(answer);
};

const isAnswerCorrect = (question, userAnswer) => {
  if (!GRADED_TYPES.includes(question.question_type)) return null;
  const correct = question.correct_answers || [];
  if (question.question_type === "multi_select") {
    const sorted = (userAnswer || []).slice().sort();
    const expected = correct.slice().sort();
    return JSON.stringify(sorted) === JSON.stringify(expected);
  }
  return userAnswer === correct[0];
};

const AttemptAnswerDetails = ({ attempt, questionMap }) => {
  const answers = attempt.answers || {};
  const orderedIds = Object.keys(questionMap).filter((id) => id in answers);

  return (
    <div className="tw-mt-3 tw-space-y-2 tw-border-t tw-border-gray-100 tw-pt-3">
      <p className="tw-text-xs tw-font-semibold tw-text-gray-600">Question-by-question breakdown</p>
      {orderedIds.map((qId) => {
        const q = questionMap[qId];
        if (!q) return null;
        const userAnswer = answers[qId];
        const userDisplay = formatAnswer(userAnswer, q);
        const graded = GRADED_TYPES.includes(q.question_type);
        const correct = graded ? isAnswerCorrect(q, userAnswer) : null;
        const correctDisplay =
          graded && q.correct_answers?.length
            ? (q.correct_answers || [])
                .map((a) => {
                  const opt = (q.options || []).find((o) => o.id === a);
                  return opt ? opt.text : a;
                })
                .join(", ")
            : null;

        return (
          <div
            key={qId}
            className={`tw-rounded-lg tw-p-3 tw-text-sm ${
              graded ? (correct ? "tw-bg-green-50/80" : "tw-bg-red-50/80") : "tw-bg-gray-50"
            }`}
          >
            <div className="tw-mb-1.5 tw-font-medium tw-text-gray-700">
              <RichTextViewer content={q.question_text} className="[&_p]:tw-my-0 [&_p]:tw-inline" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1.5 tw-text-xs sm:tw-flex-row sm:tw-flex-wrap sm:tw-items-start sm:tw-gap-x-4">
              <div className="tw-min-w-0 tw-flex-1">
                <span className="tw-font-medium tw-text-gray-500">User:</span>{" "}
                <span className="tw-break-words tw-text-gray-800">{userDisplay || "—"}</span>
              </div>
              {graded && correctDisplay != null && (
                <div className="tw-min-w-0 tw-flex-1">
                  <span className="tw-font-medium tw-text-gray-500">Correct:</span>{" "}
                  <span className={`tw-break-words ${correct ? "tw-text-green-700" : "tw-text-red-700"}`}>
                    {correctDisplay}
                  </span>
                </div>
              )}
              {graded && (
                <span className="tw-ml-auto tw-font-medium">
                  {correct === true ? (
                    <span className="tw-text-green-600">✓ Correct</span>
                  ) : correct === false ? (
                    <span className="tw-text-red-600">✗ Incorrect</span>
                  ) : null}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AttemptRow = ({ attempt, questionMap }) => {
  const [showDetails, setShowDetails] = useState(false);
  const hasAnswers = attempt.answers && Object.keys(attempt.answers).length > 0;

  return (
    <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-overflow-hidden">
      <div
        className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 ${hasAnswers ? "tw-cursor-pointer hover:tw-bg-gray-50" : ""}`}
        onClick={() => hasAnswers && setShowDetails((v) => !v)}
      >
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-xs tw-font-semibold tw-text-gray-600">
            #{attempt.attempt_number}
          </span>
          <div>
            <p className="tw-text-sm tw-font-medium tw-text-gray-900">
              Score: {attempt.score ?? "—"}%
            </p>
            <p className="tw-text-xs tw-text-gray-500">
              {new Date(attempt.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {attempt.passed ? (
            <span className="tw-rounded-full tw-bg-green-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-700">
              Passed
            </span>
          ) : (
            <span className="tw-rounded-full tw-bg-red-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red-600">
              Failed
            </span>
          )}
          {hasAnswers && (
            <ChevronDown
              className={`tw-h-4 tw-w-4 tw-text-gray-400 tw-transition-transform ${showDetails ? "tw-rotate-180" : ""}`}
            />
          )}
        </div>
      </div>
      {showDetails && hasAnswers && questionMap && (
        <div className="tw-border-t tw-border-gray-100 tw-px-4 tw-pb-3">
          <AttemptAnswerDetails attempt={attempt} questionMap={questionMap} />
        </div>
      )}
    </div>
  );
};

const EnrollmentCard = ({ enrollment, slug, questionMap }) => {
  const [expanded, setExpanded] = useState(false);
  const [attempts, setAttempts] = useState(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (attempts !== null) return;
    setLoadingAttempts(true);
    try {
      const { data } = await getEnrollmentAttempts(slug, enrollment.id);
      setAttempts(data);
    } catch (err) {
      console.error("Failed to load attempts:", err);
      setAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white">
      <button
        onClick={handleExpand}
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-text-left hover:tw-bg-gray-50"
      >
        <div className="tw-flex tw-items-center tw-gap-4">
          {enrollment.image ? (
            <img
              src={enrollment.image}
              alt=""
              className="tw-h-10 tw-w-10 tw-rounded-full tw-object-cover"
            />
          ) : (
            <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-200 tw-text-sm tw-font-semibold tw-text-gray-500">
              {(getDisplayName(enrollment) || "?")[0]}
            </div>
          )}
          <div>
            <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
              {getDisplayName(enrollment) || getFullName(enrollment)}
            </p>
            <p className="tw-text-xs tw-text-gray-500">{enrollment.email}</p>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-hidden tw-text-right sm:tw-block">
            <StatusBadge status={enrollment.status} />
          </div>
          {enrollment.score !== null && enrollment.score !== undefined && (
            <span className="tw-hidden tw-text-sm tw-font-medium tw-text-gray-700 sm:tw-inline">
              {enrollment.score}%
            </span>
          )}
          <span className="tw-hidden tw-text-xs tw-text-gray-400 sm:tw-inline">
            {enrollment.attempt_count || 0} attempt{(enrollment.attempt_count || 0) !== 1 ? "s" : ""}
          </span>
          <span className="tw-hidden tw-text-xs tw-text-gray-400 md:tw-inline">
            {timeAgo(enrollment.updated_at)}
          </span>
          {expanded ? (
            <ChevronUp className="tw-h-4 tw-w-4 tw-text-gray-400" />
          ) : (
            <ChevronDown className="tw-h-4 tw-w-4 tw-text-gray-400" />
          )}
        </div>
      </button>

      {/* Mobile-only summary row */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-t tw-border-gray-100 tw-px-5 tw-py-2 sm:tw-hidden">
        <StatusBadge status={enrollment.status} />
        {enrollment.score !== null && enrollment.score !== undefined && (
          <span className="tw-text-xs tw-text-gray-500">{enrollment.score}%</span>
        )}
        <span className="tw-text-xs tw-text-gray-400">
          {enrollment.attempt_count || 0} attempt{(enrollment.attempt_count || 0) !== 1 ? "s" : ""}
        </span>
        <span className="tw-text-xs tw-text-gray-400">{timeAgo(enrollment.updated_at)}</span>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="tw-overflow-hidden"
          >
            <div className="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-p-5">
              <h4 className="tw-mb-3 tw-text-sm tw-font-semibold tw-text-gray-700">
                Attempt History
              </h4>

              {loadingAttempts && (
                <div className="tw-space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="tw-h-14 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
                  ))}
                </div>
              )}

              {!loadingAttempts && attempts && attempts.length === 0 && (
                <p className="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">
                  No completion attempts yet (user hasn't submitted the quiz)
                </p>
              )}

              {!loadingAttempts && attempts && attempts.length > 0 && (
                <div className="tw-space-y-2">
                  {attempts.map((attempt) => (
                    <AttemptRow key={attempt.id} attempt={attempt} questionMap={questionMap} />
                  ))}
                </div>
              )}

              {/* Current answers preview */}
              {enrollment.answers && Object.keys(enrollment.answers).length > 0 && questionMap && (
                <div className="tw-mt-4">
                  <h4 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">
                    Current Answers
                  </h4>
                  <div className="tw-max-h-64 tw-space-y-2 tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-3">
                    {Object.entries(enrollment.answers).map(([qId, answer]) => {
                      const q = questionMap[qId];
                      if (!q) return null;
                      const plainText = (q.question_text || "").replace(/<[^>]*>/g, "");
                      let displayAnswer = answer;
                      if (Array.isArray(answer)) {
                        displayAnswer = answer
                          .map((a) => {
                            const opt = (q.options || []).find((o) => o.id === a);
                            return opt ? opt.text : a;
                          })
                          .join(", ");
                      } else if (typeof answer === "string" && q.options) {
                        const opt = (q.options || []).find((o) => o.id === answer);
                        if (opt) displayAnswer = opt.text;
                      }
                      return (
                        <div key={qId} className="tw-border-b tw-border-gray-100 tw-pb-2 last:tw-border-0">
                          <p className="tw-text-xs tw-font-medium tw-text-gray-600 tw-line-clamp-1">
                            {plainText}
                          </p>
                          <p className="tw-mt-0.5 tw-text-xs tw-text-gray-500 tw-line-clamp-2">
                            {typeof displayAnswer === "string" ? displayAnswer : JSON.stringify(displayAnswer)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CourseEnrollments = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");

  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/courses");
      return;
    }
    const init = async () => {
      try {
        const [courseRes, enrollRes] = await Promise.all([
          getCourse(slug),
          listEnrollments(slug),
        ]);
        setCourse(courseRes.data);
        setEnrollments(enrollRes.data);
      } catch (err) {
        console.error("Failed to load:", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [slug, user, navigate]);

  const questionMap = useMemo(() => {
    if (!course) return {};
    const map = {};
    (course.sections || []).forEach((s) =>
      (s.questions || []).forEach((q) => {
        map[q.id] = q;
      })
    );
    return map;
  }, [course]);

  const completedCount = enrollments.filter((e) => e.status === "completed").length;
  const inProgressCount = enrollments.filter((e) => e.status === "in_progress").length;

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-gray-50">
        <div className="tw-w-full tw-max-w-2xl tw-space-y-4 tw-px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="tw-h-20 tw-animate-pulse tw-rounded-xl tw-bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-pt-[70px] tw-text-gray-900 md:tw-pt-[86px]">
      {/* Top bar */}
      <div className="tw-sticky tw-top-[70px] tw-z-30 tw-border-b tw-border-gray-200 tw-bg-white md:tw-top-[86px]">
        <div className="tw-container tw-mx-auto tw-flex tw-max-w-4xl tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <button
            onClick={() => navigate("/courses")}
            className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-text-gray-900"
          >
            <ArrowLeft className="tw-h-4 tw-w-4" />
            Back
          </button>
          <h2 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate tw-max-w-[200px] sm:tw-max-w-none">
            {course?.title || "Enrollments"}
          </h2>
          <div className="tw-w-16" />
        </div>
      </div>

      <div className="tw-container tw-mx-auto tw-max-w-4xl tw-px-4 tw-py-6">
        {/* Stats */}
        <div className="tw-mb-6 tw-grid tw-grid-cols-3 tw-gap-3">
          <div className="tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 tw-p-4 tw-text-center">
            <Users className="tw-mx-auto tw-mb-1 tw-h-5 tw-w-5 tw-text-gray-400" />
            <p className="tw-text-2xl tw-font-bold tw-text-gray-900">{enrollments.length}</p>
            <p className="tw-text-xs tw-text-gray-500">Total Enrolled</p>
          </div>
          <div className="tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 tw-p-4 tw-text-center">
            <Trophy className="tw-mx-auto tw-mb-1 tw-h-5 tw-w-5 tw-text-green-500" />
            <p className="tw-text-2xl tw-font-bold tw-text-green-600">{completedCount}</p>
            <p className="tw-text-xs tw-text-gray-500">Completed</p>
          </div>
          <div className="tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 tw-p-4 tw-text-center">
            <Clock className="tw-mx-auto tw-mb-1 tw-h-5 tw-w-5 tw-text-yellow-500" />
            <p className="tw-text-2xl tw-font-bold tw-text-yellow-600">{inProgressCount}</p>
            <p className="tw-text-xs tw-text-gray-500">In Progress</p>
          </div>
        </div>

        {/* Enrollment list */}
        {enrollments.length === 0 ? (
          <div className="tw-rounded-2xl tw-bg-white tw-border tw-border-gray-200 tw-py-16 tw-text-center">
            <Users className="tw-mx-auto tw-mb-3 tw-h-10 tw-w-10 tw-text-gray-300" />
            <p className="tw-text-gray-500">No enrollments yet</p>
          </div>
        ) : (
          <div className="tw-space-y-3">
            {enrollments.map((enrollment) => (
              <EnrollmentCard
                key={enrollment.id}
                enrollment={enrollment}
                slug={slug}
                questionMap={questionMap}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseEnrollments;
